import type {
    TourConfig,
    TourStep,
} from "~/features/onboarding/types/tour-config";

// AIDEV-NOTE: DPMP® guided tours — triggered by useDpmpPartsTour / useDpmpPartDetailTour
// (features/packages/hooks/use-dpmp-tours.ts), which react to the enable-dpmp flag turning
// on: immediately on the page where the user enabled it, and on their NEXT visit to the
// other surface. Copy deliberately opens "With DPMP® on…" so users associate the new
// card content (age/gender on every part!) with the package they enabled.
//
// VARIANTS, not runtime conditions: the tour overlay cannot skip a step whose target is
// missing from the DOM (TourStep.condition is never evaluated at runtime), so each
// optional concern (voice picker present? exile part present?) gets its own config and
// the hooks pick one after DOM checks. The exile step also exists standalone
// (dpmp-parts-exile-tour) so a user whose first DPMP tour ran with no exiles gets that
// one step the first time they visit /parts after an exile appears.
//
// AIDEV-NOTE: The DPMP® Parts Map step (MAP_STEP) comes FIRST in every parts-page tour
// (per Adam, Sep 2026) so the tour opens on the map. Users who finished the DPMP parts
// tour before the map shipped get no follow-up step (open question in the map plan).

/**
 * First VISIBLE match — mirrors how the tour overlay resolves its target.
 *
 * AIDEV-NOTE: Comma-separated selectors are tried IN ORDER, each exhausted before the
 * next. A single querySelectorAll with the same commas would return DOM order instead,
 * which silently defeats "prefer a populated card, fall back to any card" — the fallback
 * would win whenever it happened to sit earlier in the grid.
 */
function findVisible(selector: string): HTMLElement | null {
    if (typeof document === "undefined") return null;
    for (const candidate of selector.split(",")) {
        const trimmed = candidate.trim();
        if (!trimmed) continue;
        const match = Array.from(document.querySelectorAll(trimmed)).find(
            (el) => (el as HTMLElement).offsetParent !== null,
        ) as HTMLElement | undefined;
        if (match) return match;
    }
    return null;
}

// AIDEV-NOTE: The flip steps physically flip the highlighted card via its own flip
// button (same element the overlay highlights: first visible match), then flip it
// back on exit so the grid isn't left in a flipped state.
function flipCard(cardSelector: string, face: "back" | "front"): void {
    const card = findVisible(cardSelector);
    if (!card) return;
    const label = face === "back" ? "Show healing record" : "Show part front";
    card.querySelector<HTMLElement>(`button[aria-label='${label}']`)?.click();
}

// AIDEV-NOTE: Prefer a card whose DPMP fields are populated — findVisible takes the first
// match in DOM order, so without the `-filled` variant first the tour happily demonstrates
// the healing record on a card that has none. Falls back to any card of the right type.
const PROTECTOR_CARD_SELECTOR =
    "[data-dpmp-card='protector'][data-dpmp-filled], [data-dpmp-card='protector']";
const EXILE_CARD_SELECTOR =
    "[data-dpmp-card='exile'][data-dpmp-filled], [data-dpmp-card='exile']";

// --- Parts-page steps ---------------------------------------------------------------

function identityStep(extraContent = ""): TourStep {
    return {
        id: "dpmp-catalog-card",
        targetSelector: "[data-tour-id='first-part-card']",
        title: "DPMP® Parts Catalog Cards",
        // AIDEV-NOTE: Renders inside the tooltip's <p> — the block span makes the
        // blank-line paragraph break, and the closer is italic (Adam, Sep 2026).
        content: (
            <>
                With DPMP® on, each card shows the part&rsquo;s name, type,
                age, and gender — the identity fields from its Parts Catalogue
                Card.
                {extraContent}
                <span className="mt-4 block italic">
                    Settings &gt; Add-Ons to Enable / Disable.
                </span>
            </>
        ),
        position: "bottom",
    };
}

// AIDEV-NOTE: Sidebar "Show Part Age"/"Show Part Gender" checkboxes (DPMP-only). The
// first-part-card fallback keeps the tour from stranding if the sidebar isn't in the
// DOM (it should be — tours are desktop-only and /parts always renders it there).
const DISPLAY_OPTIONS_STEP: TourStep = {
    id: "dpmp-display-options",
    targetSelector:
        "[data-tour-id='dpmp-display-options'], [data-tour-id='first-part-card']",
    title: "Show or hide age and gender",
    content:
        "Age and gender on your cards come with DPMP®. Untick either box to hide that field.",
    position: "right",
};

// AIDEV-NOTE: Falls back to the first part card if no protector-type part exists
// (rare — the copy still reads sensibly against any card back).
const PROTECTOR_STEP: TourStep = {
    id: "dpmp-protector-flip",
    targetSelector: `${PROTECTOR_CARD_SELECTOR}, [data-tour-id='first-part-card']`,
    title: "Protector roles",
    content:
        "The flip side of a protector's card tracks its role — the job it has carried — and the new role it takes on as it heals.",
    position: "bottom",
    onEnter: () =>
        flipCard(
            `${PROTECTOR_CARD_SELECTOR}, [data-tour-id='first-part-card']`,
            "back",
        ),
    onExit: () =>
        flipCard(
            `${PROTECTOR_CARD_SELECTOR}, [data-tour-id='first-part-card']`,
            "front",
        ),
};

// AIDEV-NOTE: Targets the panel MASTHEAD, which renders even while the map is collapsed
// or its edges are still loading — so the step always has a target.
const MAP_STEP: TourStep = {
    id: "dpmp-parts-map",
    targetSelector: "[data-tour-id='dpmp-parts-map']",
    title: "DPMP® Parts Map",
    content:
        "Your parts, placed the way Michelle Glass's maps place them: exiles at the center, managers around them, firefighters on the outside. Hover a part to see who it's in relation with.",
    position: "bottom",
};

const EXILE_STEP: TourStep = {
    id: "dpmp-exile-flip",
    targetSelector: EXILE_CARD_SELECTOR,
    title: "Exile healing steps",
    content:
        "For exiles, the flip side holds the part's story and its DPMP® healing record — where it was retrieved from, what it unburdened, and the qualities it reclaimed.",
    position: "bottom",
    onEnter: () => flipCard(EXILE_CARD_SELECTOR, "back"),
    onExit: () => flipCard(EXILE_CARD_SELECTOR, "front"),
};

function withFinish(steps: TourStep[]): TourStep[] {
    return steps.map((step, index) =>
        index === steps.length - 1
            ? { ...step, finishButtonText: "Done" }
            : step,
    );
}

const DPMP_WELCOME = {
    title: "DPMP® is on",
    description: "A quick look at what changed on your parts.",
    startButtonText: "Show me",
    skipButtonText: "Skip",
};

// --- DPMP-only parts-page tours (user already knows the parts page) -------------------

export const dpmpPartsTourConfig: TourConfig = {
    id: "dpmp-parts-tour",
    page: "/parts",
    welcome: DPMP_WELCOME,
    steps: withFinish([
        MAP_STEP,
        identityStep(),
        DISPLAY_OPTIONS_STEP,
        PROTECTOR_STEP,
    ]),
};

export const dpmpPartsTourWithExileConfig: TourConfig = {
    id: "dpmp-parts-tour-exile",
    page: "/parts",
    welcome: DPMP_WELCOME,
    steps: withFinish([
        MAP_STEP,
        identityStep(),
        DISPLAY_OPTIONS_STEP,
        PROTECTOR_STEP,
        EXILE_STEP,
    ]),
};

// AIDEV-NOTE: One-step follow-up for users whose DPMP parts tour ran before they had
// any exile parts.
export const dpmpPartsExileTourConfig: TourConfig = {
    id: "dpmp-parts-exile-tour",
    page: "/parts",
    welcome: DPMP_WELCOME,
    steps: withFinish([EXILE_STEP]),
};

// --- Merged new-user tours (baseline parts tour + DPMP woven together) ----------------
// AIDEV-NOTE: A new user with DPMP already on gets ONE tour: the baseline parts-tour
// steps (timeline, filters) with the DPMP card steps replacing the baseline card step
// (its pin/link content is folded into the identity step). Completing or skipping a
// merged tour counts as seeing BOTH the baseline and the DPMP core (see
// use-dpmp-tours.ts), so neither re-fires.

const TIMELINE_STEP: TourStep = {
    id: "vine-timeline",
    targetSelector: "[data-tour-id='parts-timeline']",
    title: "Your Parts Timeline",
    content:
        "Your parts are shown here in order of most-recent appearance in your session(s).",
    position: "right",
};

const FILTERS_STEP: TourStep = {
    id: "filters",
    targetSelector: "[data-tour-id='parts-filters']",
    title: "Filter Your Parts",
    content:
        "By default, parts you briefly mentioned are hidden. Use the filters to display more or fewer parts.",
    position: "bottom",
    highlightState: "expanded",
};

const MERGED_IDENTITY_EXTRA =
    " Hover a card to pin it, or check two or more parts to link duplicates.";

const MERGED_WELCOME = {
    title: "A Home for Your Parts",
    description: "Take a brief tour of the Parts feature",
    startButtonText: "Take Tour",
    skipButtonText: "Skip",
};

export const partsDpmpMergedTourConfig: TourConfig = {
    id: "parts-tour-dpmp",
    page: "/parts",
    welcome: MERGED_WELCOME,
    steps: withFinish([
        MAP_STEP,
        TIMELINE_STEP,
        identityStep(MERGED_IDENTITY_EXTRA),
        DISPLAY_OPTIONS_STEP,
        PROTECTOR_STEP,
        FILTERS_STEP,
    ]),
};

export const partsDpmpMergedWithExileTourConfig: TourConfig = {
    id: "parts-tour-dpmp-exile",
    page: "/parts",
    welcome: MERGED_WELCOME,
    steps: withFinish([
        MAP_STEP,
        TIMELINE_STEP,
        identityStep(MERGED_IDENTITY_EXTRA),
        DISPLAY_OPTIONS_STEP,
        PROTECTOR_STEP,
        EXILE_STEP,
        FILTERS_STEP,
    ]),
};

// --- Part-detail tours ----------------------------------------------------------------

// AIDEV-NOTE: Anchored on the Connect button, not a voice dropdown — the picker lives in the
// setup overlay that button opens (the inline dropdown was removed 2026-09-10).
const VOICE_STEP: TourStep = {
    id: "michelle-voice",
    targetSelector: "[data-tour-id='part-meditation-connect']",
    title: "Michelle Glass's voice",
    content:
        "With DPMP® on, your meditations can play in the voice of the practice's author. Press Connect with Part and choose “Michelle Glass” under Voice — you can listen to a sample first.",
    position: "bottom",
};

const HEALING_STEP: TourStep = {
    id: "healing-steps",
    targetSelector: "[data-tour-id='part-healing-steps']",
    title: "Healing steps",
    content:
        "Each part now carries its DPMP® healing record — a new role for protectors; retrieval, unburdening, and reclaimed qualities for exiles. Capture them here or through the Parts Catalogue Card.",
    position: "right",
};

export const dpmpPartDetailTourConfig: TourConfig = {
    id: "dpmp-part-detail-tour",
    page: "/parts/:partId",
    welcome: DPMP_WELCOME,
    steps: withFinish([VOICE_STEP, HEALING_STEP]),
};

// AIDEV-NOTE: Variant for accounts without meditations enabled — the voice picker isn't
// rendered, so the tour goes straight to the healing steps.
export const dpmpPartDetailBasicTourConfig: TourConfig = {
    id: "dpmp-part-detail-tour-basic",
    page: "/parts/:partId",
    welcome: DPMP_WELCOME,
    steps: withFinish([HEALING_STEP]),
};

export { findVisible as findVisibleTourTarget };
